import { useContext } from "react";

import { Box, SxProps, Theme } from "@mui/material";
import { ORGANIZATION_COUNTRY_MAP } from "@simplyk/common";
import { FieldValues, Path, useFormContext } from "react-hook-form";

import { useLocaleContext } from "../../../../../../common/contexts/LocaleContext";
import { getNestedErrorMessage } from "../../../../../../common/helpers/reactHookForm";
import { isEmailAndAllowPlus, isRequired } from "../../../../../../common/helpers/validators";
import { useFormV2Context } from "../../../contexts/FormV2Context";
import { getQuestionLabel } from "../../../helpers/getLabel";

import { QuestionFieldDate } from "./QuestionFieldDate";
import { QuestionFieldAddress } from "./QuestionFieldAddress";
import { QuestionFieldMultipleCheckboxes } from "./QuestionFieldMultipleCheckboxes";
import { QuestionFieldName } from "./QuestionFieldName";
import { QuestionFieldNotificationPhone } from "./QuestionFieldNotificationPhone";
import { QuestionFieldPhone } from "./QuestionFieldPhone";
import { QuestionFieldSelect } from "./QuestionFieldSelect";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { QuestionOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";

export interface Props<T extends FieldValues> {
  name: Path<T>;
  question: QuestionOutput;
  id?: string;
  sx?: SxProps<Theme>;
  nameFieldSpacing?: number;
}

export function QuestionField<T extends FieldValues>({ question, name, id, sx, nameFieldSpacing }: Props<T>) {
  const { locale } = useLocaleContext();
  const { formCountry } = useFormV2Context();
  const { isPreview } = usePreviewContext();
  const { t } = useTranslate();
  const { organization } = useContext(FrontendFormContext);
  const form = useFormContext<T>();
  const { formState, register } = form;
  let label = question.type === "Address" ? t("ticketing", "question.fullAddress") : getQuestionLabel(question, locale);
  const required = Boolean(question?.required);

  const textName = `${name}.answers.0` as Path<T>;
  const questionIdField = `${name}.questionId` as Path<T>;

  let textErrorMessage = getNestedErrorMessage(textName, formState);

  // We prevent errors from being shown in the live preview
  if (isPreview) {
    textErrorMessage = undefined;
  }

  let isGhostPreview = false;
  if (isPreview && label === "" && question.type !== "Name") {
    label = t("common", "toFill");
    isGhostPreview = true;
  }

  const renderInputField = () => {
    switch (question.type) {
      case "Email":
        return (
          <FormTextField
            control={form.control}
            name={textName}
            rules={[
              isRequired(question.required, t("common", "answerRequired")),
              isEmailAndAllowPlus(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
            ]}
            label={label}
            error={Boolean(textErrorMessage)}
            helperText={textErrorMessage}
            type="email"
            data-test="question-field-email"
            disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
            required={required}
          />
        );

      case "PhoneNumber":
        return (
          <QuestionFieldPhone
            name={textName}
            form={form}
            label={label}
            data-test="question-field-phone-number"
            question={question}
            country={ORGANIZATION_COUNTRY_MAP[formCountry].phoneInputCountry}
          />
        );

      case "NotificationPhoneNumber":
        return <QuestionFieldNotificationPhone name={name} question={question} />;

      case "Address":
        return (
          <QuestionFieldAddress form={form} name={name} label={label} required={required} organization={organization} />
        );
      case "Date":
        return (
          <QuestionFieldDate
            name={textName}
            form={form}
            label={label}
            data-test="question-field-date"
            question={question}
          />
        );

      case "MultipleCheckboxes":
        return (
          <QuestionFieldMultipleCheckboxes
            name={`${name}.choiceIds` as Path<T>}
            question={question}
            form={form}
            label={label}
            data-test="question-field-multiple-checkboxes"
          />
        );

      case "Checkbox":
        return (
          <FormCheckbox
            control={form.control}
            name={textName}
            rules={[isRequired(required, t("common", "answerRequired"))]}
            label={label}
            size="small"
            error={Boolean(textErrorMessage)}
            helperText={textErrorMessage}
            data-test="question-field-checkbox"
            required={required}
          />
        );

      case "Multiple":
        return (
          <QuestionFieldSelect
            name={`${name}.choiceIds` as Path<T>}
            question={question}
            form={form}
            label={label}
            isPreview={isPreview}
          />
        );

      case "Name":
        return (
          <QuestionFieldName
            spacing={nameFieldSpacing}
            name={textName}
            form={form}
            question={question}
            errorMessage={textErrorMessage}
            isPreview={isPreview}
          />
        );

      default:
        return (
          <FormTextField
            control={form.control}
            name={textName}
            rules={[isRequired(required, t("common", "answerRequired"))]}
            label={label}
            error={Boolean(textErrorMessage)}
            helperText={textErrorMessage}
            data-test="question-field-text"
            disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
            required={required}
          />
        );
    }
  };

  return (
    <Box data-test="question-field" sx={isGhostPreview ? { ...sx, opacity: GHOST_PREVIEW_OPACITY } : sx} id={id}>
      {/* Hidden input to hold question id */}
      <input type="hidden" {...register(questionIdField)} value={question.id} />

      {renderInputField()}
    </Box>
  );
}
